import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Typography,
} from "@mui/material";
import {
    CheckRounded,
    Close,
    ContentCopyOutlined,
    FileDownloadOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import BackdropOverlay from "../common/BackdropOverlay";
import {
    DIALOG_ACCEPT_BUTTON_SX,
    DIALOG_CANCEL_BUTTON_SX,
    DIALOG_PAPER_RADIUS,
} from "../common/dialogActionButtonSx";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import { pushToast } from "../../stores/toastStore";
import { ACCOUNT_PAGE_MOBILE_MQ } from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();
const CODE_LENGTH = 6;
const TOTAL_STEPS = 3;

const DIALOG_FADE_IN = {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
};

const DIALOG_PAPER_SX = {
    "@keyframes dialogFadeIn": { ...DIALOG_FADE_IN },
    position: "relative",
    width: { xs: "100%", sm: "30rem" },
    maxWidth: "30rem",
    overflow: "hidden",
    background: `
        linear-gradient(
            165deg,
            rgba(18,14,14,0.98) 0%,
            rgba(10,10,12,0.99) 48%,
            rgba(8,8,10,1) 100%
        )
    `,
    borderRadius: DIALOG_PAPER_RADIUS,
    border: `1px solid ${colors.border.default}`,
    boxShadow: "0 28px 64px rgba(0, 0, 0, 0.55)",
    animation: "dialogFadeIn 0.2s ease-in-out",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.55)}, transparent)`,
        pointerEvents: "none",
    },
};

const STEP_EYEBROW_SX = {
    color: colors.primary.main,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    mb: "0.35rem",
};

const STEP_TITLE_SX = {
    color: colors.text.primary,
    fontSize: "1.2rem",
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: "0.01em",
};

const STEP_SUBTITLE_SX = {
    color: colors.text.secondary,
    fontSize: "0.85rem",
    lineHeight: 1.5,
    mt: "0.4rem",
};

const DIALOG_ACTION_ROW_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: "0.55rem", sm: "0.65rem" },
    mt: "1.35rem",
};

const DIALOG_ACTION_BUTTON_SX = {
    flex: { xs: "none", sm: 1 },
    width: { xs: "100%", sm: "auto" },
    minWidth: 0,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        width: "100%",
        flex: "none",
    },
};

const QR_IMAGE_SIZE = "7.95rem";

const QR_OUTER_SX = {
    p: "0.45rem",
    borderRadius: "18px",
    display: "inline-flex",
    flexShrink: 0,
    background: "linear-gradient(145deg, #f6d488, #dfa53c 55%, #a97a2c)",
    boxShadow: "0 12px 26px rgba(0, 0, 0, 0.4)",
};

const QR_INNER_SX = {
    p: "0.7rem",
    borderRadius: "13px",
    display: "inline-flex",
    flexShrink: 0,
    background: "linear-gradient(160deg, #171209, #0d0a06)",
};

const QR_BOX_SX = {
    background: "#fff",
    borderRadius: "8px",
    p: "0.15rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    flexShrink: 0,
};

function QrCodeFrame({ children }) {
    return (
        <Box
            sx={{
                mt: "1.5rem",
                display: "flex",
                justifyContent: "center",
            }}
        >
            <Box sx={QR_OUTER_SX}>
                <Box sx={QR_INNER_SX}>
                    <Box sx={QR_BOX_SX}>{children}</Box>
                </Box>
            </Box>
        </Box>
    );
}

QrCodeFrame.propTypes = {
    children: PropTypes.node,
};

function formatManualKeyDisplay(key) {
    if (!key) return "";
    const cleaned = String(key).replace(/\s/g, "").toUpperCase();
    return cleaned.match(/.{1,4}/g)?.join("-") ?? cleaned;
}

async function postTwoFactor(path, body) {
    const res = await fetch(`${basePath}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
        throw new Error(data?.message || "Request failed");
    }
    return data;
}

/** Dedupes concurrent /setup calls (e.g. React Strict Mode double effects). */
let activeSetupRequest = null;
let cachedSetupResult = null;
let cachedSetupAt = 0;
const SETUP_CACHE_MS = 5000;

function clearSetupCache() {
    cachedSetupResult = null;
    cachedSetupAt = 0;
}

function requestSetupOnce() {
    const now = Date.now();
    if (cachedSetupResult && now - cachedSetupAt < SETUP_CACHE_MS) {
        return Promise.resolve(cachedSetupResult);
    }

    if (!activeSetupRequest) {
        activeSetupRequest = postTwoFactor("/auth/2fa/setup", {})
            .then((data) => {
                cachedSetupResult = data;
                cachedSetupAt = Date.now();
                return data;
            })
            .finally(() => {
                activeSetupRequest = null;
            });
    }
    return activeSetupRequest;
}

function StepProgressDots({ step }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
            {Array.from({ length: TOTAL_STEPS }).map((_, index) => {
                const stepNumber = index + 1;
                const isDone = stepNumber < step;
                const isActive = stepNumber === step;
                return (
                    <Box
                        key={stepNumber}
                        aria-hidden
                        sx={{
                            width: "1.35rem",
                            height: "0.25rem",
                            borderRadius: "2px",
                            transition: "background 0.25s ease-in-out",
                            background: isDone
                                ? colors.state.success.main
                                : isActive
                                  ? `linear-gradient(90deg, ${colors.primary.active}, ${colors.primary.main})`
                                  : "rgba(255,255,255,0.08)",
                        }}
                    />
                );
            })}
        </Box>
    );
}

StepProgressDots.propTypes = {
    step: PropTypes.number.isRequired,
};

function SixDigitCodeInput({ value, onChange, disabled, autoFocus = false }) {
    const inputRefs = useRef([]);

    useEffect(() => {
        if (!autoFocus) return;
        const timer = setTimeout(() => inputRefs.current[0]?.focus(), 120);
        return () => clearTimeout(timer);
    }, [autoFocus]);

    const digits = value.padEnd(CODE_LENGTH, " ").slice(0, CODE_LENGTH).split("");

    const updateAtIndex = (index, char) => {
        const next = digits.map((digit, i) => (i === index ? char : digit.trim())).join("");
        onChange(next.replace(/\s/g, "").slice(0, CODE_LENGTH));
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                gap: "0.55rem",
                mt: "1.35rem",
            }}
        >
            {digits.map((digit, index) => (
                <Box
                    key={`code-digit-${index}`}
                    component="input"
                    ref={(node) => {
                        inputRefs.current[index] = node;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={digit.trim()}
                    disabled={disabled}
                    aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
                    onChange={(event) => {
                        const char = event.target.value.replace(/\D/g, "").slice(-1);
                        updateAtIndex(index, char);
                        if (char && index < CODE_LENGTH - 1) {
                            inputRefs.current[index + 1]?.focus();
                        }
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Backspace" && !digit.trim() && index > 0) {
                            inputRefs.current[index - 1]?.focus();
                        }
                    }}
                    onPaste={(event) => {
                        event.preventDefault();
                        const pasted = event.clipboardData
                            .getData("text")
                            .replace(/\D/g, "")
                            .slice(0, CODE_LENGTH);
                        if (pasted) {
                            onChange(pasted);
                            const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
                            inputRefs.current[focusIndex]?.focus();
                        }
                    }}
                    sx={{
                        width: "2.75rem",
                        height: "3.35rem",
                        textAlign: "center",
                        fontSize: "1.3rem",
                        fontWeight: 600,
                        fontFamily: "inherit",
                        color: colors.text.primary,
                        background: "rgba(255,255,255,0.03)",
                        border: `1px solid ${colors.border.default}`,
                        borderRadius: "10px",
                        outline: "none",
                        transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out",
                        "&:focus": {
                            borderColor: colors.primary.main,
                            background: "rgba(255,255,255,0.05)",
                        },
                        "&:disabled": {
                            opacity: 0.55,
                            cursor: "not-allowed",
                        },
                    }}
                />
            ))}
        </Box>
    );
}

SixDigitCodeInput.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
};

function RecoveryCodesGrid({ codes }) {
    return (
        <Box
            sx={{
                mt: "1.1rem",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0.5rem",
                p: "1rem",
                borderRadius: "10px",
                border: `1px solid ${colors.border.default}`,
                background: "rgba(255,255,255,0.03)",
            }}
        >
            {codes.map((code) => (
                <Typography
                    key={code}
                    sx={{
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                        color: colors.text.secondary,
                        letterSpacing: "0.02em",
                        textAlign: "center",
                    }}
                >
                    {code}
                </Typography>
            ))}
        </Box>
    );
}

RecoveryCodesGrid.propTypes = {
    codes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default function TwoFactorAuthActivationStepsDialog({
    open,
    onClose,
    onComplete,
}) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState(null);
    const [manualKey, setManualKey] = useState(null);
    const [verifyCode, setVerifyCode] = useState("");
    const [backupCodes, setBackupCodes] = useState(null);

    const resetState = useCallback(() => {
        setStep(1);
        setLoading(false);
        setError(null);
        setQrCodeUrl(null);
        setManualKey(null);
        setVerifyCode("");
        setBackupCodes(null);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onClose?.();
    }, [onClose, resetState]);

    const startSetup = useCallback(async ({ signal } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const data = await requestSetupOnce();
            if (signal?.aborted) {
                return;
            }
            setQrCodeUrl(data.qrCodeUrl);
            setManualKey(data.manualEntryKey);
        } catch (err) {
            if (signal?.aborted) {
                return;
            }
            setError(err.message || "Failed to start 2FA setup");
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!open) {
            clearSetupCache();
            return undefined;
        }

        const controller = new AbortController();
        resetState();
        startSetup({ signal: controller.signal });

        return () => {
            controller.abort();
        };
    }, [open, resetState, startSetup]);

    const handleCopyKey = async () => {
        if (!manualKey) return;
        try {
            await navigator.clipboard.writeText(manualKey);
            pushToast({ type: "success", message: "Setup key copied" });
        } catch {
            pushToast({ type: "error", message: "Could not copy setup key" });
        }
    };

    const handleCopyRecoveryCodes = async () => {
        if (!backupCodes?.length) return;
        try {
            await navigator.clipboard.writeText(backupCodes.join("\n"));
            pushToast({ type: "success", message: "Recovery codes copied" });
        } catch {
            pushToast({ type: "error", message: "Could not copy recovery codes" });
        }
    };

    const handleDownloadRecoveryCodes = () => {
        if (!backupCodes?.length) return;
        const text =
            "Ouro 2FA Backup Codes\n" +
            "========================\n" +
            "Save these codes in a safe place.\n" +
            "Each code can only be used once.\n\n" +
            backupCodes.join("\n");
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "ouro-backup-codes.txt";
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const handleVerifySetup = async () => {
        if (verifyCode.length < CODE_LENGTH) return;
        setLoading(true);
        setError(null);
        try {
            const data = await postTwoFactor("/auth/2fa/verify-setup", {
                code: verifyCode.trim(),
            });
            setBackupCodes(data.backupCodes);
            setStep(3);
        } catch (err) {
            setError(err.message || "Invalid verification code");
        } finally {
            setLoading(false);
        }
    };

    const handleFinish = () => {
        handleClose();
        onComplete?.();
    };

    if (!open) return null;

    const primaryButtonSx = {
        ...DIALOG_ACCEPT_BUTTON_SX,
        ...DIALOG_ACTION_BUTTON_SX,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.4rem",
        minHeight: "2.35rem",
        px: "1.15rem",
        fontSize: "0.82rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "none",
        whiteSpace: { xs: "normal", sm: "nowrap" },
    };

    const secondaryButtonSx = {
        ...DIALOG_CANCEL_BUTTON_SX,
        ...DIALOG_ACTION_BUTTON_SX,
        minHeight: "2.35rem",
        fontSize: "0.78rem",
        letterSpacing: "0.04em",
        textTransform: "none",
    };

    return (
        <BackdropOverlay open onClick={handleClose}>
            <Box role="dialog" aria-modal="true" aria-labelledby="two-factor-setup-title" sx={DIALOG_PAPER_SX}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: "1.85rem",
                        pt: "1.5rem",
                        pb: "0.35rem",
                    }}
                >
                    <StepProgressDots step={step} />
                    <IconButton
                        onClick={handleClose}
                        aria-label="Close two-factor setup dialog"
                        sx={{
                            width: "1.75rem",
                            height: "1.75rem",
                            borderRadius: "0.55rem",
                            color: colors.text.secondary,
                            backgroundColor: "rgba(255, 255, 255, 0.04)",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                            },
                        }}
                    >
                        <Close sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                </Box>

                <Box sx={{ px: "1.85rem", pb: "1.65rem", pt: "0.65rem" }}>
                    {step === 1 ? (
                        <>
                            <Typography sx={STEP_EYEBROW_SX}>Step 1 of 3</Typography>
                            <Typography id="two-factor-setup-title" sx={STEP_TITLE_SX}>
                                Scan the QR code
                            </Typography>
                            <Typography sx={STEP_SUBTITLE_SX}>
                                Open your authenticator app and scan this code, or enter the key
                                manually.
                            </Typography>

                            <QrCodeFrame>
                                {loading && !qrCodeUrl ? (
                                    <Box
                                        sx={{
                                            width: QR_IMAGE_SIZE,
                                            height: QR_IMAGE_SIZE,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : qrCodeUrl ? (
                                    <Box
                                        component="img"
                                        src={qrCodeUrl}
                                        alt="Two-factor authentication QR code"
                                        sx={{
                                            width: QR_IMAGE_SIZE,
                                            height: QR_IMAGE_SIZE,
                                            display: "block",
                                            objectFit: "contain",
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: QR_IMAGE_SIZE,
                                            height: QR_IMAGE_SIZE,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CircularProgress size={28} />
                                    </Box>
                                )}
                            </QrCodeFrame>

                            {manualKey ? (
                                <Box
                                    sx={{
                                        mt: "1.1rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.65rem",
                                        p: "0.75rem 0.85rem",
                                        borderRadius: "10px",
                                        border: `1px solid ${colors.border.default}`,
                                        background: "rgba(255,255,255,0.03)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontFamily: "monospace",
                                            fontSize: "0.78rem",
                                            color: colors.text.secondary,
                                            letterSpacing: "0.03em",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {formatManualKeyDisplay(manualKey)}
                                    </Typography>
                                    <IconButton
                                        onClick={handleCopyKey}
                                        aria-label="Copy setup key"
                                        sx={{
                                            width: "2rem",
                                            height: "2rem",
                                            flexShrink: 0,
                                            borderRadius: "8px",
                                            border: `1px solid ${colors.border.default}`,
                                            color: colors.text.secondary,
                                            background: "rgba(255,255,255,0.04)",
                                            "&:hover": {
                                                color: colors.primary.main,
                                                borderColor: alpha(colors.primary.main, 0.35),
                                            },
                                        }}
                                    >
                                        <ContentCopyOutlined sx={{ fontSize: "0.95rem" }} />
                                    </IconButton>
                                </Box>
                            ) : null}

                            {error ? (
                                <Typography
                                    role="alert"
                                    sx={{
                                        mt: "0.85rem",
                                        color: colors.state.danger.main,
                                        fontSize: "0.78rem",
                                        lineHeight: 1.45,
                                    }}
                                >
                                    {error}
                                </Typography>
                            ) : null}

                            <Box sx={{ mt: "1.35rem" }}>
                                <Button
                                    onClick={() => setStep(2)}
                                    disabled={loading || !qrCodeUrl}
                                    sx={{ ...primaryButtonSx, width: "100%" }}
                                >
                                    I&apos;ve scanned it
                                </Button>
                            </Box>
                        </>
                    ) : null}

                    {step === 2 ? (
                        <>
                            <Typography sx={STEP_EYEBROW_SX}>Step 2 of 3</Typography>
                            <Typography id="two-factor-setup-title" sx={STEP_TITLE_SX}>
                                Enter the 6-digit code
                            </Typography>
                            <Typography sx={STEP_SUBTITLE_SX}>
                                Type the current code shown in your authenticator app to confirm the
                                pairing.
                            </Typography>

                            <SixDigitCodeInput
                                value={verifyCode}
                                onChange={setVerifyCode}
                                disabled={loading}
                                autoFocus
                            />

                            {error ? (
                                <Typography
                                    role="alert"
                                    sx={{
                                        mt: "0.85rem",
                                        color: colors.state.danger.main,
                                        fontSize: "0.78rem",
                                        lineHeight: 1.45,
                                        textAlign: "center",
                                    }}
                                >
                                    {error}
                                </Typography>
                            ) : null}

                            <Box sx={DIALOG_ACTION_ROW_SX}>
                                <Button
                                    onClick={() => {
                                        setError(null);
                                        setVerifyCode("");
                                        setStep(1);
                                    }}
                                    disabled={loading}
                                    sx={secondaryButtonSx}
                                >
                                    Back
                                </Button>
                                <Button
                                    onClick={handleVerifySetup}
                                    disabled={loading || verifyCode.length < CODE_LENGTH}
                                    sx={primaryButtonSx}
                                >
                                    {loading ? (
                                        <CircularProgress size={16} sx={{ color: "inherit" }} />
                                    ) : null}
                                    Verify &amp; continue
                                </Button>
                            </Box>
                        </>
                    ) : null}

                    {step === 3 && backupCodes ? (
                        <>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: "50%",
                                    mx: "auto",
                                    mb: "0.85rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: alpha(colors.state.success.main, 0.13),
                                    border: `1px solid ${alpha(colors.state.success.main, 0.35)}`,
                                    color: colors.state.success.main,
                                }}
                            >
                                <CheckRounded sx={{ fontSize: "1.5rem" }} />
                            </Box>
                            <Typography sx={{ ...STEP_EYEBROW_SX, textAlign: "center" }}>
                                Step 3 of 3
                            </Typography>
                            <Typography
                                id="two-factor-setup-title"
                                sx={{ ...STEP_TITLE_SX, textAlign: "center" }}
                            >
                                Save your recovery codes
                            </Typography>
                            <Typography sx={{ ...STEP_SUBTITLE_SX, textAlign: "center" }}>
                                Each code works once if you lose access to your authenticator. Store
                                them somewhere safe — you won&apos;t see them again.
                            </Typography>

                            <RecoveryCodesGrid codes={backupCodes} />

                            <Box sx={DIALOG_ACTION_ROW_SX}>
                                <Button
                                    onClick={handleCopyRecoveryCodes}
                                    sx={secondaryButtonSx}
                                    startIcon={<ContentCopyOutlined sx={{ fontSize: "0.95rem" }} />}
                                >
                                    Copy
                                </Button>
                                <Button
                                    onClick={handleDownloadRecoveryCodes}
                                    sx={secondaryButtonSx}
                                    startIcon={<FileDownloadOutlined sx={{ fontSize: "0.95rem" }} />}
                                >
                                    Download
                                </Button>
                            </Box>

                            <Box sx={{ mt: "0.65rem" }}>
                                <Button onClick={handleFinish} sx={{ ...primaryButtonSx, width: "100%" }}>
                                    Done
                                </Button>
                            </Box>
                        </>
                    ) : null}
                </Box>
            </Box>
        </BackdropOverlay>
    );
}

TwoFactorAuthActivationStepsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onComplete: PropTypes.func,
};
